// Static cost analysis (SCR-11, ARCH §27.3). PURE (ENG-1) — AST arithmetic only, no
// host, no world, no clock.
//
// ARCH §27.3, BINDING, quoted verbatim:
//
//   "`sim/script/lang/cost.ts` computes a **worst-case op bound** from the AST: a
//    statement costs 1 + its children; `repeat n {b}` costs
//    `min(n, SCR.REPEAT_MAX) × cost(b)`; `if/else` costs
//    `cost(cond) + max(cost(then), cost(else))`; a call costs `1 + cost(callee body)`,
//    finite because the graph is acyclic; a verb costs 1 (host-side work is bounded
//    separately by `FILL_MAX`/`SETBLOCK_MAX`).
//
//    **A save whose bound exceeds the target host's per-dispatch budget is REFUSED** —
//    `tryCompile` returns `{ok:false, error}` naming the computed op count and the
//    offending line. Not a warning. The bound is **tight**, not pessimistic: without
//    `break`, `repeat n` runs exactly `n` times, so only `if/else` over-estimates.
//    Runtime abort (E-30) remains the backstop for host-side cost."
//
// The bound is finite because the grammar has no `while`, no `break` and — after
// `parse.ts`'s `assertAcyclic` DFS — no recursion: every `repeat` count is either a
// compile-time constant or clamped to `SCR.REPEAT_MAX` exactly as the VM clamps it
// (`vm.ts` `case 'repeat'`), and every call unfolds into a finite callee body.
//
// Two numbers come out of one walk (`CostResult`): the total worst-case op count, and
// the 1-based source line at which the running total crossed the budget (the "offending
// line" the refusal names) — or, for a program that fits, the last line counted. The
// walk stays O(AST): a subtree whose measured cost cannot cross the budget is folded in
// arithmetically, and only the subtree that does cross is descended into, so a
// `repeat 4096 {…}` never iterates 4096 times to find its line.
//
// Deliberate estimates, both documented as such (nothing else over- or under-counts):
//   • `if/else` charges the LARGER branch — the one over-estimate §27.3 names.
//   • a `return` inside a `repeat` body leaves the loop early (`vm.ts` breaks on
//     `returning`), so a fn whose loop returns mid-way can run fewer than n turns; the
//     bound still charges all n, which is the worst case and errs high.
//   • `self.x` / `event.power` charge 2 (member node + its base) where the VM charges
//     1 (it answers a namespace read without evaluating the base). Over-counting by 1
//     per namespace read is safe for a refusal bound and avoids re-deriving scopes here
//     just to learn whether a local has shadowed `self`.
//
// AMENDED by D-TF-171 (John agreed 2026-09-19): §27.3 as first written said
// `repeat n {b}` costs `min(n, REPEAT_MAX) × cost(b)`, which sat n ops BELOW the VM,
// because `vm.ts` also charges one `step()` per loop TURN. That made the bound UNSOUND —
// a script could clear the save-time refusal and still abort on the op budget, which is
// precisely what §27.3 and §27.18 promise cannot happen. The turn is now charged:
// `min(n, REPEAT_MAX) × (1 + cost(b))`. The bound consequently DOMINATES the VM
// everywhere and is EXACT for loops (measured: `repeat 10 { say("x") }` = 42 = `run()`;
// `repeat 3 { repeat 3 { say("x") } }` = 47 = `run()`), so clearing the gate is a real
// guarantee. `tests/script_cost.test.ts` pins domination across seven shapes — if that
// test ever fails, the refusal is lying and the budget must go back to opt-in.
import { SCR } from '../../config';
import { ScriptError } from './lex';
import type { Pos } from './lex';
import type { Expr, FnDecl, Program, Stmt } from './parse';

export interface CostResult {
  /** Worst-case op count for the whole program. */
  ops: number;
  /** 1-based source line that pushed the program over budget, or the last line counted. */
  line: number;
}

/** Worst-case op bound for a compiled program, per ARCH §27.3. Total and deterministic.
 *  `budget` only decides which line the result reports (the crossing line); the op
 *  count never depends on it. */
export function programCost(program: Program, budget: number = SCR.OP_BUDGET): CostResult {
  return bodyCost(program.body, fnTable(program.fns), budget);
}

/** Worst-case op bound for one statement list (a handler or fn body). */
export function bodyCost(
  body: readonly Stmt[],
  fns: ReadonlyMap<string, FnDecl>,
  budget: number = SCR.OP_BUDGET,
): CostResult {
  const ctx: Ctx = { fns, memo: new Map(), active: new Set(), budget, total: 0, line: 1, overLine: 0 };
  walkStmts(body, ctx);
  return { ops: ctx.total, line: ctx.overLine !== 0 ? ctx.overLine : ctx.line };
}

/** `Program.fns` (declaration order) as the by-name table the cost walk calls through.
 *  A duplicate name cannot occur — `parse.ts` refuses a redeclared `fn`. */
export function fnTable(fns: readonly FnDecl[]): ReadonlyMap<string, FnDecl> {
  const table = new Map<string, FnDecl>();
  for (const fn of fns) table.set(fn.name, fn);
  return table;
}

/** True when a bound exceeds the host's per-dispatch budget ⇒ the save is REFUSED. */
export function overBudget(cost: CostResult, budget: number = SCR.OP_BUDGET): boolean {
  return cost.ops > budget;
}

/** The refusal copy: the computed op count and the offending line (ARCH §27.3,
 *  SCRIPTING §4.7 — "refused at save, with the computed number and the offending
 *  line"). */
export function overBudgetMessage(cost: CostResult, budget: number = SCR.OP_BUDGET): string {
  return `script too expensive: worst case ${cost.ops} ops exceeds the ${budget}-op budget (line ${cost.line})`;
}

/** The refusal as the one error type the script pipeline raises. No `pos` — the line is
 *  in the message, and a bound has no column (the crossing is an op, not a character). */
export function overBudgetError(cost: CostResult, budget: number = SCR.OP_BUDGET): ScriptError {
  return new ScriptError(overBudgetMessage(cost, budget));
}

// ── Walk state ────────────────────────────────────────────────────────────────
interface Ctx {
  readonly fns: ReadonlyMap<string, FnDecl>;
  /** fn name → its body's cost. Memoized so a diamond call graph stays linear. */
  readonly memo: Map<string, number>;
  /** fns currently being measured/walked. `parse.ts` proves the graph acyclic, so a
   *  re-entry is unreachable; the guard keeps this module total regardless. */
  readonly active: Set<string>;
  readonly budget: number;
  total: number;
  line: number;
  /** 0 until `total` crosses `budget`; then the line that crossed it. */
  overLine: number;
}

/** The statement fields the walk reads. Written structurally so a statement kind added
 *  by a later milestone still costs `1 + its children` instead of breaking the build:
 *  `let`/`assign`/`expr`/`return` carry `expr`, `on` carries `body`, and a `kind:'fn'`
 *  declaration executes nothing inline (its cost folds into its callers). */
interface StmtLike {
  readonly kind: string;
  readonly pos: Pos;
  readonly expr?: Expr;
  readonly cond?: Expr;
  readonly count?: Expr;
  readonly then?: readonly Stmt[];
  readonly otherwise?: readonly Stmt[];
  readonly body?: readonly Stmt[];
}

const NO_STMTS: readonly Stmt[] = [];

// ── The bound itself (no line tracking — pure arithmetic) ─────────────────────
function stmtsCost(body: readonly Stmt[], ctx: Ctx): number {
  let ops = 0;
  for (const s of body) ops += stmtCost(s, ctx);
  return ops;
}

function stmtCost(s: Stmt, ctx: Ctx): number {
  const g: StmtLike = s;
  if (g.kind === 'fn') return 0; // a declaration runs nothing where it stands
  if (g.kind === 'if') {
    // cost(cond) + max(cost(then), cost(else)) — the one over-estimate (§27.3).
    const cond = g.cond ? exprCost(g.cond, ctx) : 0;
    return 1 + cond + Math.max(stmtsCost(g.then ?? NO_STMTS, ctx), stmtsCost(g.otherwise ?? NO_STMTS, ctx));
  }
  if (g.kind === 'repeat') {
    // min(n, SCR.REPEAT_MAX) × (1 + cost(b)) — §27.3 as AMENDED by D-TF-171. The
    // `1 +` is the per-turn `step()` `vm.ts` spends (`for (…) { this.step(); … }`),
    // which §27.3's original `× cost(b)` omitted and which made the bound UNSOUND:
    // a script could clear the save-time refusal and still abort on the op budget.
    // With the turn charged, the bound is EXACT for a loop, not merely dominant.
    const count = g.count ? exprCost(g.count, ctx) : 0;
    const turns = g.count ? repeatTurns(g.count) : 0;
    return 1 + count + turns * (1 + stmtsCost(g.body ?? NO_STMTS, ctx));
  }
  let ops = 1; // the statement itself
  if (g.expr) ops += exprCost(g.expr, ctx);
  if (g.body) ops += stmtsCost(g.body, ctx); // `on <event> {…}`: the handler may fire
  return ops;
}

function exprCost(e: Expr, ctx: Ctx): number {
  switch (e.kind) {
    case 'num': case 'str': case 'bool': case 'var': return 1;
    case 'unary': return 1 + exprCost(e.operand, ctx);
    // `&&`/`||` short-circuit at runtime; the worst case evaluates both sides.
    case 'binary': return 1 + exprCost(e.left, ctx) + exprCost(e.right, ctx);
    case 'member': return 1 + exprCost(e.obj, ctx);
    case 'call': {
      let ops = 1; // a verb costs 1 (§27.3) — host-side work is bounded by FILL_MAX
      for (const a of e.args) ops += exprCost(a, ctx);
      const fn = calleeFn(e, ctx);
      if (fn) ops += fnBodyCost(fn, ctx); // a call costs 1 + cost(callee body)
      return ops;
    }
    default: return 1;
  }
}

function fnBodyCost(fn: FnDecl, ctx: Ctx): number {
  const memo = ctx.memo.get(fn.name);
  if (memo !== undefined) return memo;
  if (ctx.active.has(fn.name)) return 0; // unreachable: parse.ts refuses any cycle
  ctx.active.add(fn.name);
  const ops = stmtsCost(fn.body, ctx);
  ctx.active.delete(fn.name);
  ctx.memo.set(fn.name, ops);
  return ops;
}

/** The callee's declaration, or undefined for a verb. Name resolution mirrors
 *  `parse.ts`/`vm.ts`: a bare ident, or `ns.prop` for a namespaced verb (never a fn). */
function calleeFn(e: Expr & { kind: 'call' }, ctx: Ctx): FnDecl | undefined {
  const callee = e.callee;
  if (callee.kind !== 'var') return undefined;
  return ctx.fns.get(callee.name);
}

/** Worst-case turns of a `repeat`, clamped exactly as `vm.ts` clamps `n`: a count the
 *  compiler cannot fold is assumed to ask for the most the VM would allow. */
function repeatTurns(count: Expr): number {
  const folded = foldNumber(count);
  let n = Math.floor(folded ?? SCR.REPEAT_MAX);
  if (!Number.isFinite(n) || n < 0) n = 0;
  if (n > SCR.REPEAT_MAX) n = SCR.REPEAT_MAX;
  return n;
}

/** Constant-fold a numeric expression, or undefined if it is not known at compile time.
 *  Folding keeps the bound tight for the common `repeat 10 {…}` and `repeat 4 * 4 {…}`. */
function foldNumber(e: Expr): number | undefined {
  if (e.kind === 'num') return e.value;
  if (e.kind === 'unary') {
    const v = foldNumber(e.operand);
    return e.op === '-' && v !== undefined ? -v : undefined;
  }
  if (e.kind === 'binary') {
    const l = foldNumber(e.left);
    const r = foldNumber(e.right);
    if (l === undefined || r === undefined) return undefined;
    switch (e.op) {
      case '+': return l + r;
      case '-': return l - r;
      case '*': return l * r;
      case '/': return l / r;
      case '%': return l % r;
      default: return undefined;
    }
  }
  return undefined;
}

// ── Locating the offending line ───────────────────────────────────────────────
// Every walk function charges EXACTLY the cost its `*Cost` twin returns, so the total
// is the bound and the line is a by-product. A subtree that cannot cross the budget is
// folded in at its own line; only the subtree that crosses is descended into.

function fold(ctx: Ctx, ops: number, line: number): void {
  if (ops <= 0) return;
  ctx.total += ops;
  ctx.line = line;
  if (ctx.overLine === 0 && ctx.total > ctx.budget) ctx.overLine = line;
}

function walkStmts(body: readonly Stmt[], ctx: Ctx): void {
  for (const s of body) walkStmt(s, ctx);
}

function walkStmt(s: Stmt, ctx: Ctx): void {
  const g: StmtLike = s;
  const ops = stmtCost(s, ctx);
  if (ctx.overLine !== 0 || ctx.total + ops <= ctx.budget) { fold(ctx, ops, g.pos.line); return; }
  if (g.kind === 'fn') return;
  fold(ctx, 1, g.pos.line); // the statement itself
  if (g.kind === 'if') {
    if (g.cond) walkExpr(g.cond, ctx);
    const then = g.then ?? NO_STMTS;
    const otherwise = g.otherwise ?? NO_STMTS;
    walkStmts(stmtsCost(then, ctx) >= stmtsCost(otherwise, ctx) ? then : otherwise, ctx);
    return;
  }
  if (g.kind === 'repeat') {
    if (g.count) walkExpr(g.count, ctx);
    walkRepeatBody(g, ctx);
    return;
  }
  if (g.expr) walkExpr(g.expr, ctx);
  if (g.body) walkStmts(g.body, ctx);
}

/** Turns that fit under the budget fold in arithmetically; the one that crosses it is
 *  walked, so the reported line is the offending line inside the loop body. */
function walkRepeatBody(g: StmtLike, ctx: Ctx): void {
  const body = g.body ?? NO_STMTS;
  // `1 +` is the turn's own step (D-TF-171), so an empty body still costs its turns.
  const per = 1 + stmtsCost(body, ctx);
  const turns = g.count ? repeatTurns(g.count) : 0;
  if (turns === 0) return;
  const room = Math.max(0, Math.floor((ctx.budget - ctx.total) / per));
  const fits = ctx.overLine !== 0 ? turns : Math.min(turns, room);
  fold(ctx, fits * per, g.pos.line);
  if (fits < turns) {
    fold(ctx, 1, g.pos.line); // the crossing turn's own step, before its body
    walkStmts(body, ctx);     // the crossing turn
    fold(ctx, (turns - fits - 1) * per, ctx.line);
  }
}

function walkExpr(e: Expr, ctx: Ctx): void {
  const ops = exprCost(e, ctx);
  if (ctx.overLine !== 0 || ctx.total + ops <= ctx.budget) { fold(ctx, ops, e.pos.line); return; }
  fold(ctx, 1, e.pos.line); // the expression node itself
  switch (e.kind) {
    case 'unary': walkExpr(e.operand, ctx); return;
    case 'binary': walkExpr(e.left, ctx); walkExpr(e.right, ctx); return;
    case 'member': walkExpr(e.obj, ctx); return;
    case 'call': {
      for (const a of e.args) walkExpr(a, ctx);
      const fn = calleeFn(e, ctx);
      if (!fn || ctx.active.has(fn.name)) return;
      ctx.active.add(fn.name);
      walkStmts(fn.body, ctx);
      ctx.active.delete(fn.name);
      return;
    }
    default: return; // num / str / bool / var — no children
  }
}
